import React, { Fragment, useRef, useState, useEffect } from 'react'
import { Box, Typography, TextField, Chip, InputAdornment, Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { StepLoader } from '../../components'
import { stepperStyles } from '../styles'
import { SkuItem } from './SkuItem'
import { useApi } from '../../../../hooks/useApi'
import {
  validateSkuOnInventoryCheck,
  closeInventoryCheckPositionCycle
} from '../../api'
import { Status } from '../../../../hooks/useFetchState'
import { LoadingButton } from '../../../../components/_EOS/Button/LoadingButton'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { Product } from '../../../Productos/entities/Product'
import { StepProps } from '../../../../components/_WMS/Stepper'
import { ScanIcon } from '../../../../components/_WMS/_icons/ScanIcon'
import { CameraScanButton } from '../../../../components/_WMS/CameraScan'
import { useScanKeyboard } from '../../../../hooks/useScanKeyboard'
import {
  eInventoryCheckSteps,
  SharedStateType,
  SkuBatchInfoDto
} from '../../types'
import { Modal } from '../../../../components/_EOS/Modal'
import { ListBatch } from './ListBatch'
import { useModal } from '../../../../hooks/useModal'
import { useProductOrigins } from '../../../../hooks'
import {
  ExpiredColorStatus,
  getExpiredStatusToday,
  getStyleRow
} from '../../../../utils/colors'

interface SkuBrandOption extends SkuBatchInfoDto {
  brandName?: string
}

interface ScanSkuProps extends StepProps<SharedStateType> {}


export const ScanSku: React.FC<ScanSkuProps> = ({
  sharedState: {
    skuList,
    workOrderId,
    positionCode,
    positionId,
    inventoryCheckDetailId,
    selectedSku,
    batchList,
    zoneName,
    warehouseName
  },
  setSharedState,
  goToStep
}) => {
  const skuInputRef = useRef<HTMLInputElement>(null)
  // En modo camara el teclado virtual no se levanta salvo que toquen el input.
  const { autoFocus } = useScanKeyboard()
  const { state, callApi } = useApi<ApiResponse<Product>>()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const {
    state: postCloseInventoryCheckItemsState,
    callApi: postCloseInventoryCheckItemsCallApi
  } = useApi<ApiResponse<Product>>()

  const [scannedSku, setScannedSku] = useState('')
  const [skuError, setSkuError] = useState('')
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true)

  useEffect(() => {
    if ((skuList?.length ?? 0) === 1) {
      setIsHeaderExpanded(false)
    }
  }, [skuList?.length])
  const [brandOptions, setBrandOptions] = useState<SkuBrandOption[]>([])
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(0)
  const [showBrandModal, setShowBrandModal] = useState(false)

  const isApiLoading =
    state.status === Status.Loading ||
    postCloseInventoryCheckItemsState.status === Status.Loading

     useEffect(() => {
      setSharedState((prev) => ({ ...prev, stepTitle: 'Escanea los productos de la posición' }))
      return () => {
        setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
      }
    }, [])

  const requestValidateSkuOnInventoryCheck = async (
    sku: string
  ): Promise<SkuBrandOption[] | null> => {
    if (!workOrderId) return null
    return await callApi(validateSkuOnInventoryCheck(sku, positionId || 0))
      .then((data) => {
        if (Array.isArray(data)) return data as SkuBrandOption[]
        return [data as SkuBrandOption]
      })
      .catch((error) => {
        setSharedState((prevState) => ({ ...prevState, error }))
        return null
      })
  }

  const requestGetCloseInventoryCheckPositionCycle = () => {
    const list = (skuList || []).map((el) => ({
      skuId: el.skuId || 0,
      checkedQty: el.checkedQty,
      batchId: el.batchId
    }))
    postCloseInventoryCheckItemsCallApi(
      closeInventoryCheckPositionCycle(inventoryCheckDetailId || 0, list)
    )
      .then(() => {
        setSharedState((prevState) => ({ ...prevState, skuList: undefined }))
        goToStep(eInventoryCheckSteps.SuccessInventoryCheck)
      })
      .catch((err) => {
        setSharedState((prevState) => ({
          ...prevState,
          error: err,
          closePositionFailed: true
        }))
        goToStep(eInventoryCheckSteps.Error)
      })
  }

  const processSelectedSku = (
    item: SkuBrandOption
  ) => {
    if (item.batches?.length) {
      setSharedState((prev) => ({
        ...prev,
        selectedSku: item,
        batchList: item.batches
      }))
      selectBatchModalState.openModal('sm', { id: item.skuId }, '')
      return
    }

    setSharedState((prev) => {
      // Recalcular dentro del callback para usar siempre el estado más reciente
      const existingIdx = (prev.skuList ?? []).findIndex(
        (s) => s.skuId === item.skuId && !s.batchId
      )

      const updatedSkuList =
        existingIdx >= 0
          ? prev.skuList!.map((sku, idx) =>
              idx === existingIdx
                ? {
                    ...sku,
                    checkedQty: sku.checkedQty + 1,
                    validated: true
                  }
                : sku
            )
          : [
              ...(prev.skuList || []),
              {
                sku: item.sku,
                ean13: item.ean13,
                skuId: item.skuId,
                checkedQty: 1,
                validated: true,
                productOriginId: item.productOriginId,
                brandName: item.brandName
              }
            ]
      return { ...prev, skuList: updatedSkuList }
    })
    // En modo camara no se reenfoca: abriria el teclado virtual entre lecturas.
    if (autoFocus) skuInputRef.current?.focus()
  }

  const handleBrandConfirm = () => {
    const selected = brandOptions[selectedBrandIndex]
    setShowBrandModal(false)
    processSelectedSku(selected)
  }

  const handleScanSku = async (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    await runScanSku(scannedSku)
  }

  /**
   * Procesa el SKU/EAN recibido por parametro y no del estado: la camara llama a
   * setScannedSku y a esto en el mismo tick, y setState es asincrono, asi que
   * leer del estado usaria el valor anterior.
   */
  const runScanSku = async (scannedSku: string) => {
    if (scannedSku.length <= 0) {
      setSkuError('Ingresa un SKU o EAN antes de continuar')
      return
    }
    setSkuError('')

    const existingSkuIndex =
      skuList?.findIndex(
        (item) => item.sku === scannedSku || item.ean13 === scannedSku
      ) ?? -1
    const withBatch = skuList?.find(
      (it) => it.sku === scannedSku || it.ean13 === scannedSku
    )?.batchId

    // SKU de marca única ya validado: incrementar sin llamar a la API
    if (existingSkuIndex >= 0 && !withBatch) {
      const existingSku = skuList![existingSkuIndex]
      if (existingSku.validated) {
        setSharedState((prev) => ({
          ...prev,
          skuList: prev.skuList!.map((item, index) =>
            index === existingSkuIndex
              ? { ...item, checkedQty: item.checkedQty + 1 }
              : item
          )
        }))
        setScannedSku('')
      } else {
        alert('SKU/EAN no está validado')
      }
      return
    }

    const results = await requestValidateSkuOnInventoryCheck(scannedSku)
    if (!results || results.length === 0) {
      goToStep(eInventoryCheckSteps.Error)
      return
    }

    setScannedSku('')

    if (results.length > 1) {
      setBrandOptions(results)
      setSelectedBrandIndex(0)
      setShowBrandModal(true)
      return
    }

    processSelectedSku(results[0])
  }

  const {
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const closeModal = () => {
    selectBatchModalState.closeModal()
    // En modo camara no se reenfoca: abriria el teclado virtual.
    if (autoFocus) skuInputRef.current?.focus()
  }

  const handleDeleteSku = (sku: string) => {
    setSharedState((prev) => ({
      ...prev,
      skuList: prev.skuList?.filter((item) => item.sku !== sku)
    }))
  }

  const selectBatchModalState = useModal<{ id: number }>('')

  const getBackgroundStyle = (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => {
    if (expirationDate) {
      return getStyleRow(
        getExpiredStatusToday(expirationDate, skuProductOrigin)
      )
    }
    return { backgroundColor: '#f5f5f5', borderBottom: '1px solid #ccc' }
  }

  const getTextColor = (
    expirationDate: string | undefined,
    skuProductOrigin: number
  ) => {
    if (expirationDate) {
      const status = getExpiredStatusToday(expirationDate, skuProductOrigin)
      if (status === ExpiredColorStatus.expired) return '#c62828'
      if (status === ExpiredColorStatus.expireSoon) return '#e79f31'
    }
    return '#5C8E2F'
  }

  return (
    <Fragment>
      <Box sx={stepperStyles.root}>
        {isApiLoading && <StepLoader message='Procesando SKU...' />}
        <Box
          sx={stepperStyles.content}
          style={{
            justifyContent: 'flex-start',
            paddingTop: 24,
            overflowY: 'auto'
          }}
        >
          <Box style={{ width: '100%', padding: '0 16px' }}>           
            {/* Cabecera expandible */}
            <Box
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '10px 16px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                marginBottom: 16
              }}
            >
              {/* Chips siempre visibles: Conteo y Posición */}
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { label: 'Conteo', value: workOrderId ?? '—' },
                  { label: 'Posición', value: positionCode ?? '—' }
                ].map(({ label, value }) => (
                  <Chip
                    key={label}
                    label={`${label}: ${value}`}
                    color='primary'
                    variant='outlined'
                    size='small'
                    sx={{                                            
                      fontSize: '0.8125rem',
                      width: '100%'
                    }}
                  />
                ))}
              </Box>

              {/* Chips colapsables: Zona y Almacén */}
              <Collapse in={isHeaderExpanded}>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  {[
                    { label: 'Zona', value: zoneName ?? '—' },
                    { label: 'Almacén', value: warehouseName ?? '—' }
                  ].map(({ label, value }) => (
                    <Chip
                      key={label}
                      label={`${label}: ${value}`}
                      color='primary'
                      variant='outlined'
                      size='small'
                      sx={{ 
                        fontSize: '0.8125rem',
                        width: '100%'
                      }}
                    />
                  ))}
                </Box>
              </Collapse>

              {/* Toggle Ver más / Ver menos */}
              <Box
                onClick={() => setIsHeaderExpanded((prev) => !prev)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginTop: 8,
                  cursor: 'pointer',
                  gap: 2
                }}
              >
                <Typography
                  variant='caption'
                  style={{ color: '#1976d2', fontWeight: 600 }}
                >
                  {isHeaderExpanded ? 'Ver menos' : 'Ver más'}
                </Typography>
                {isHeaderExpanded
                  ? <ExpandLessIcon style={{ fontSize: 18, color: '#1976d2' }} />
                  : <ExpandMoreIcon style={{ fontSize: 18, color: '#1976d2' }} />
                }
              </Box>
            </Box>

            {/* Input + botones */}
            <form onSubmit={handleScanSku}>
              <TextField
                inputRef={skuInputRef}
                autoFocus={autoFocus}
                fullWidth
                variant='outlined'
                placeholder='Escanea o escribe un SKU/EAN'
                value={scannedSku}
                onChange={(e) => {
                  setScannedSku(e.target.value)
                  if (skuError) setSkuError('')
                }}
                error={!!skuError}
                helperText={skuError || undefined}
                disabled={isApiLoading}
                size='small'
                style={{ marginBottom: 12 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <ScanIcon />
                    </InputAdornment>
                  )
                }}
              />
              <Box sx={{ mb: 1.5 }}>
                <CameraScanButton
                  inputRef={skuInputRef}
                  onScan={(value) => {
                    const scanned = value.trim()
                    setScannedSku(scanned)
                    setSkuError('')
                    runScanSku(scanned)
                  }}
                  onCancel={() => autoFocus && setTimeout(() => skuInputRef.current?.focus(), 0)}
                  disabled={isApiLoading}
                  dialogTitle='Escanear SKU o EAN'
                  helperText='Centra el código del producto dentro del recuadro'
                />
              </Box>

              <LoadingButton
                loading={state.status === Status.Loading}
                variant='contained'
                color='primary'
                fullWidth
                onClick={handleScanSku}
                disabled={isApiLoading}
                style={{
                  borderRadius: 24,
                  textTransform: 'none',
                  fontWeight: 700,
                  marginBottom: 10
                }}
              >
                Aceptar
              </LoadingButton>
            </form>

            <LoadingButton
              loading={
                postCloseInventoryCheckItemsState.status === Status.Loading
              }
              variant='contained'
              fullWidth
              onClick={() => setShowConfirmModal(true)}
              disabled={isApiLoading}
              style={{
                borderRadius: 24,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: '#16a34a',
                color: '#fff'
              }}
            >
              Finalizar
            </LoadingButton>

            {/* Lista de SKUs escaneados */}
            {skuList && skuList.length > 0 && (
              <Box style={{ marginTop: 24 }}>
                <Typography
                  variant='caption'
                  style={{
                    color: '#999',
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: 8
                  }}
                >
                  Productos escaneados
                </Typography>
                {skuList.map((sku, index) => (
                  <SkuItem
                    key={index}
                    sku={sku}
                    onDelete={handleDeleteSku}
                    disabled={isApiLoading}
                    productOrigins={productOrigins}
                    getBackgroundStyle={getBackgroundStyle}
                    getTextColor={getTextColor}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Modal open={showConfirmModal} hideHeader maxWidth='xs'>
        <Box style={{ textAlign: 'center', padding: '8px 0' }}>
          <Typography variant='h6' style={{ fontWeight: 700, marginBottom: 8 }}>
            ¿Finalizar auditoría de posición {positionCode}?
          </Typography>
          <Typography
            variant='body2'
            style={{ color: '#666', marginBottom: 24 }}
          >
            Se registrarán los productos escaneados y no podrás reabrir la
            posición.
          </Typography>
          <LoadingButton
            loading={
              postCloseInventoryCheckItemsState.status === Status.Loading
            }
            variant='contained'
            fullWidth
            onClick={() => {
              setShowConfirmModal(false)
              requestGetCloseInventoryCheckPositionCycle()
            }}
            style={{
              borderRadius: 24,
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#16a34a',
              color: '#fff',
              marginBottom: 10
            }}
          >
            Confirmar
          </LoadingButton>
          <LoadingButton
            loading={false}
            variant='outlined'
            color='inherit'
            fullWidth
            onClick={() => setShowConfirmModal(false)}
            style={{ borderRadius: 24, textTransform: 'none' }}
          >
            Cancelar
          </LoadingButton>
        </Box>
      </Modal>

      {/* Modal selección de marca */}
      <Modal open={showBrandModal} hideHeader maxWidth='xs'>
        <Box style={{ padding: '8px 0' }}>
          <Typography
            variant='h6'
            style={{ fontWeight: 700, marginBottom: 12 }}
          >
            Seleccionar Marca
          </Typography>

          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: 16
            }}
          >
            {brandOptions[0]?.sku && (
              <Chip
                label={`SKU: ${brandOptions[0].sku}`}
                size='small'
                variant='outlined'
                color='primary'
                style={{ fontWeight: 600 }}
              />
            )}
            <Chip
              label={`Posición: ${positionCode ?? '—'}`}
              size='small'
              variant='outlined'
              color='primary'
              style={{ fontWeight: 600 }}
            />
          </Box>

          <Typography
            variant='caption'
            style={{
              color: '#999',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: 8
            }}
          >
            Marcas disponibles
          </Typography>

          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: 20
            }}
          >
            {brandOptions.map((brand, index) => {
              const isSelected = selectedBrandIndex === index
              return (
                <Box
                  key={brand.skuId}
                  onClick={() => setSelectedBrandIndex(index)}
                  style={{
                    border: `2px solid ${isSelected ? '#1d4ed8' : '#e5e7eb'}`,
                    borderRadius: 12,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#eff6ff' : '#fff'
                  }}
                >
                  <Typography
                    variant='body2'
                    style={{
                      fontWeight: 700,
                      color: isSelected ? '#1d4ed8' : '#111'
                    }}
                  >
                    {brand.brandName ?? `Marca ${index + 1}`}
                  </Typography>
                  <Box
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      border: `2px solid ${isSelected ? '#1d4ed8' : '#d1d5db'}`,
                      backgroundColor: isSelected ? '#1d4ed8' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {isSelected && (
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#fff'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>

          <LoadingButton
            loading={false}
            variant='contained'
            fullWidth
            onClick={handleBrandConfirm}
            style={{
              borderRadius: 24,
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#16a34a',
              color: '#fff',
              marginBottom: 10
            }}
          >
            Confirmar
          </LoadingButton>
          <LoadingButton
            loading={false}
            variant='outlined'
            color='inherit'
            fullWidth
            onClick={() => setShowBrandModal(false)}
            style={{ borderRadius: 24, textTransform: 'none' }}
          >
            Cancelar
          </LoadingButton>
        </Box>
      </Modal>

      <Modal
        open={selectBatchModalState.isOpen}
        maxWidth={selectBatchModalState.size}
        hideHeader
        disableEnforceFocus
        disableRestoreFocus
      >
        <ListBatch
          setSharedState={setSharedState}
          handleCloseModal={closeModal}
          batchList={batchList || []}
          skuList={skuList || []}
          selectedSku={selectedSku}
          positionCode={positionCode || ''}
          getBackgroundStyle={getBackgroundStyle}
          productOrigins={productOrigins}
        />
      </Modal>
    </Fragment>
  )
}
